/**
  @module @ember-data/store
*/
import { assert } from '@ember/debug';
import { get } from '@ember/object';
import { assign } from '@ember/polyfills';
import recordDataFor from './record-data-for';
function relationshipsFor(instance) {
  var i = instance;
  // TODO this cast is not safe but it is the assumption of the current
  // state of the code. We need to update this class to handle CUSTOM_MODEL_CLASS
  // requirements.
  var recordData = i._internalModel._recordData;
  return recordData._relationships;
}
function schemaIsDSModel(schema) {
  return schema.isModel === true;
}
function relationshipStateFor(instance, propertyName) {
  return relationshipsFor(instance).get(propertyName);
}
/**
  @class Snapshot
  @private
  @constructor
  @param {Model} internalModel The model to create a snapshot from
*/
export default class Snapshot {
  constructor(options, identifier, _store) {
    this._store = _store;
    this.__attributes = null;
    this._belongsToRelationships = Object.create(null);
    this._belongsToIds = Object.create(null);
    this._hasManyRelationships = Object.create(null);
    this._hasManyIds = Object.create(null);
    this._internalModel = void 0;
    this._changedAttributes = void 0;
    this.identifier = void 0;
    this.modelName = void 0;
    this.id = void 0;
    this.include = void 0;
    this.adapterOptions = void 0;
    var internalModel = this._internalModel = _store._internalModelForResource(identifier);
    this.modelName = identifier.type;
    if (false /* CUSTOM_MODEL_CLASS */) {
      this.identifier = identifier;
    }
    /*
      If the internalModel does not yet have a record, then we are
      likely a snapshot being provided to a find request, so we
      populate __attributes lazily. Else, to preserve the "moment
      in time" in which a snapshot is created, we greedily grab
      the values.
     */
    if (internalModel.hasRecord) {
      this._attributes;
    }

    /**
     The id of the snapshot's underlying record
      Example
      ```javascript
     // store.push('post', { id: 1, author: 'Tomster', title: 'Ember.js rocks' });
     postSnapshot.id; // => '1'
     ```
      @property id
     @type {String}
     */
    this.id = identifier.id;

    /**
     A hash of adapter options
     @property adapterOptions
     @type {Object}
     */
    this.adapterOptions = options.adapterOptions;
    this.include = options.include;

    /**
     The name of the type of the underlying record for this snapshot, as a string.
      @property modelName
     @type {String}
     */
    this.modelName = internalModel.modelName;
    if (internalModel.hasRecord) {
      this._changedAttributes = recordDataFor(internalModel).changedAttributes();
    }
  }

  /**
   The underlying record for this snapshot. Can be used to access methods and
   properties defined on the record.
    Example
    ```javascript
   let json = snapshot.record.toJSON();
   ```
    @property record
   @type {Model}
   */
  get record() {
    return this._internalModel.getRecord();
  }
  get _attributes() {
    if (this.__attributes !== null) {
      return this.__attributes;
    }
    var record = this.record;
    var attributes = this.__attributes = Object.create(null);
    var attrs;
    if (false /* CUSTOM_MODEL_CLASS */) {
      attrs = Object.keys(this._store._attributesDefinitionFor(this.modelName, this.identifier));
    } else {
      attrs = Object.keys(this._store._attributesDefinitionFor(this.modelName));
    }
    if (false /* CUSTOM_MODEL_CLASS */) {
      attrs.forEach(keyName => {
        if (schemaIsDSModel(this.type)) {
          // if the schema is for a DSModel then the instance is too
          attributes[keyName] = get(record, keyName);
        } else {
          attributes[keyName] = recordDataFor(this._internalModel).getAttr(keyName);
        }
      });
    } else {
      // When CUSTOM_MODEL_CLASS is false `record` must be DSModel
      record.eachAttribute(keyName => attributes[keyName] = get(record, keyName));
    }
    return attributes;
  }

  /**
   The type of the underlying record for this snapshot, as a Model.
    @property type
   @type {Model}
   */
  get type() {
    return this._internalModel.modelClass;
  }
  get isNew() {
    if (!false /* CUSTOM_MODEL_CLASS */) {
      throw new Error('isNew is only available when custom model class ff is on');
    }
    return this._internalModel.isNew();
  }

  /**
   Returns the value of an attribute.
    Example
    ```javascript
   // store.push('post', { id: 1, author: 'Tomster', title: 'Ember.js rocks' });
   postSnapshot.attr('author'); // => 'Tomster'
   postSnapshot.attr('title'); // => 'Ember.js rocks'
   ```
    Note: Values are loaded eagerly and cached when the snapshot is created.
    @method attr
   @param {String} keyName
   @return {Object} The attribute value or undefined
   */
  attr(keyName) {
    if (keyName in this._attributes) {
      return this._attributes[keyName];
    }
    assert(`Model '${this.identifier}' has no attribute named '${keyName}' defined.`, false);
  }

  /**
   Returns all attributes and their corresponding values.
    Example
    ```javascript
   // store.push('post', { id: 1, author: 'Tomster', title: 'Ember.js rocks' });
   postSnapshot.attributes(); // => { author: 'Tomster', title: 'Ember.js rocks' }
   ```
    @method attributes
   @return {Object} All attributes of the current snapshot
   */
  attributes() {
    return assign({}, this._attributes);
  }

  /**
   Returns all changed attributes and their old and new values.
    Example
    ```javascript
   // store.push('post', { id: 1, author: 'Tomster', title: 'Ember.js rocks' });
   postModel.set('title', 'Ember.js rocks!');
   postSnapshot.changedAttributes(); // => { title: ['Ember.js rocks', 'Ember.js rocks!'] }
   ```
    @method changedAttributes
   @return {Object} All changed attributes of the current snapshot
   */
  changedAttributes() {
    var changedAttributes = Object.create(null);
    if (!this._changedAttributes) {
      return changedAttributes;
    }
    var changedAttributeKeys = Object.keys(this._changedAttributes);
    for (var i = 0, length = changedAttributeKeys.length; i < length; i++) {
      var key = changedAttributeKeys[i];
      changedAttributes[key] = this._changedAttributes[key].slice();
    }
    return changedAttributes;
  }

  /**
   Returns the current value of a belongsTo relationship.
    `belongsTo` takes an optional hash of options as a second parameter,
   currently supported options are:
    - `id`: set to `true` if you only want the ID of the related record to be
   returned.
    Example
    ```javascript
   // store.push('post', { id: 1, title: 'Hello World' });
   // store.createRecord('comment', { body: 'Lorem ipsum', post: post });
   commentSnapshot.belongsTo('post'); // => Snapshot
   commentSnapshot.belongsTo('post', { id: true }); // => '1'
    // store.push('comment', { id: 1, body: 'Lorem ipsum' });
   commentSnapshot.belongsTo('post'); // => undefined
   ```
    Calling `belongsTo` will return a new Snapshot as long as there's any known
   data for the relationship available, such as an ID. If the relationship is
   known but unset, `belongsTo` will return `null`. If the contents of the
   relationship is unknown `belongsTo` will return `undefined`.
    Note: Relationships are loaded lazily and cached upon first access.
    @method belongsTo
   @param {String} keyName
   @param {Object} [options]
   @return {(Snapshot|String|null|undefined)} A snapshot or ID of a known
   relationship or null if the relationship is known but unset. undefined
   will be returned if the contents of the relationship is unknown.
   */
  belongsTo(keyName, options) {
    var returnModeIsId = !!(options && options.id);
    var relationship;
    var inverseInternalModel;
    var result;
    var store = this._internalModel.store;
    if (returnModeIsId === true && keyName in this._belongsToIds) {
      return this._belongsToIds[keyName];
    }
    if (returnModeIsId === false && keyName in this._belongsToRelationships) {
      return this._belongsToRelationships[keyName];
    }
    var relationshipMeta = store._relationshipMetaFor(this.modelName, null, keyName);
    assert(`Model '${this.identifier}' has no belongsTo relationship named '${keyName}' defined.`, relationshipMeta && relationshipMeta.kind === 'belongsTo');

    // TODO @runspired it seems this code branch would not work with CUSTOM_MODEL_CLASSes

    // TODO @runspired instead of casting here either generify relationship state or
    // provide a mechanism on relationship state by which to narrow.
    relationship = relationshipStateFor(this, keyName);
    var value = relationship.getData();
    var data = value && value.data;
    inverseInternalModel = data ? store._internalModelForResource(data) : null;
    if (value && value.data !== undefined) {
      if (inverseInternalModel && !inverseInternalModel.isDeleted()) {
        if (returnModeIsId) {
          result = inverseInternalModel.id;
        } else {
          result = inverseInternalModel.createSnapshot();
        }
      } else {
        result = null;
      }
    }
    if (returnModeIsId) {
      this._belongsToIds[keyName] = result;
    } else {
      this._belongsToRelationships[keyName] = result;
    }
    return result;
  }

  /**
   Returns the current value of a hasMany relationship.
    `hasMany` takes an optional hash of options as a second parameter,
   currently supported options are:
    - `ids`: set to `true` if you only want the IDs of the related records to be
   returned.
    Example
    ```javascript
   // store.push('post', { id: 1, title: 'Hello World', comments: [2, 3] });
   postSnapshot.hasMany('comments'); // => [Snapshot, Snapshot]
   postSnapshot.hasMany('comments', { ids: true }); // => ['2', '3']
    // store.push('post', { id: 1, title: 'Hello World' });
   postSnapshot.hasMany('comments'); // => undefined
   ```
    Note: Relationships are loaded lazily and cached upon first access.
    @method hasMany
   @param {String} keyName
   @param {Object} [options]
   @return {(Array|undefined)} An array of snapshots or IDs of a known
   relationship or an empty array if the relationship is known but unset.
   undefined will be returned if the contents of the relationship is unknown.
   */
  hasMany(keyName, options) {
    var returnModeIsIds = !!(options && options.ids);
    var relationship;
    var results;
    var cachedIds = this._hasManyIds[keyName];
    var cachedSnapshots = this._hasManyRelationships[keyName];
    if (returnModeIsIds === true && keyName in this._hasManyIds) {
      return cachedIds;
    }
    if (returnModeIsIds === false && keyName in this._hasManyRelationships) {
      return cachedSnapshots;
    }
    var store = this._internalModel.store;
    var relationshipMeta = store._relationshipMetaFor(this.modelName, null, keyName);
    assert(`Model '${this.identifier}' has no hasMany relationship named '${keyName}' defined.`, relationshipMeta && relationshipMeta.kind === 'hasMany');

    // TODO @runspired it seems this code branch would not work with CUSTOM_MODEL_CLASSes

    // TODO @runspired instead of casting here either generify relationship state or
    // provide a mechanism on relationship state by which to narrow.
    relationship = relationshipStateFor(this, keyName);
    var value = relationship.getData();
    if (value.data) {
      results = [];
      value.data.forEach(member => {
        var internalModel = store._internalModelForResource(member);
        if (!internalModel.isDeleted()) {
          if (returnModeIsIds) {
            results.push(member.id);
          } else {
            results.push(internalModel.createSnapshot());
          }
        }
      });
    }

    // we assign even if `undefined` so that we don't reprocess the relationship
    // on next access. This works with the `keyName in` checks above.
    if (returnModeIsIds) {
      this._hasManyIds[keyName] = results;
    } else {
      this._hasManyRelationships[keyName] = results;
    }
    return results;
  }

  /**
    Iterates through all the attributes of the model, calling the passed
    function on each attribute.
     Example
     ```javascript
    snapshot.eachAttribute(function(name, meta) {
      // ...
    });
    ```
     @method eachAttribute
    @param {Function} callback the callback to execute
    @param {Object} [binding] the value to which the callback's `this` should be bound
  */
  eachAttribute(callback, binding) {
    if (false /* CUSTOM_MODEL_CLASS */) {
      var attrDefs = this._store._attributesDefinitionFor(this.modelName, this.identifier);
      Object.keys(attrDefs).forEach(key => {
        callback.call(binding, key, attrDefs[key]);
      });
    } else {
      // in the non CUSTOM_MODEL_CLASS world we only have DSModel instances
      this.record.eachAttribute(callback, binding);
    }
  }

  /**
    Iterates through all the relationships of the model, calling the passed
    function on each relationship.
     Example
     ```javascript
    snapshot.eachRelationship(function(name, relationship) {
      // ...
    });
    ```
     @method eachRelationship
    @param {Function} callback the callback to execute
    @param {Object} [binding] the value to which the callback's `this` should be bound
  */
  eachRelationship(callback, binding) {
    if (false /* CUSTOM_MODEL_CLASS */) {
      var relationshipDefs = this._store._relationshipsDefinitionFor(this.modelName, this.identifier);
      Object.keys(relationshipDefs).forEach(key => {
        callback.call(binding, key, relationshipDefs[key]);
      });
    } else {
      // in the non CUSTOM_MODEL_CLASS world we only have DSModel instances
      this.record.eachRelationship(callback, binding);
    }
  }

  /**
    Serializes the snapshot using the serializer for the model.
     Example
     ```app/adapters/application.js
    import Adapter from '@ember-data/adapter';
     export default Adapter.extend({
      createRecord(store, type, snapshot) {
        var data = snapshot.serialize({ includeId: true });
        var url = `/${type.modelName}`;
         return fetch(url, {
          method: 'POST',
          body: data,
        }).then((response) => response.json())
      }
    });
    ```
     @method serialize
    @param {Object} options
    @return {Object} an object whose values are primitive JSON values only
   */
  serialize(options) {
    return this._store.serializerFor(this.modelName).serialize(this, options);
  }
}